/**
 * Centralised job-sticker print helper.
 * Used by Dashboard, All Jobs and Job Detail so the print template stays in sync.
 *
 * Default (rotation = 0) uses the simplest known-working template:
 *   - no body width/height
 *   - no padding, no absolute positioning, no transforms
 *   - just a single `.sticker` element sized to the label
 * This is what was reliably printing in the correct orientation before the
 * rotation feature was added.
 *
 * For non-zero rotations (90 / 180 / 270) the layout switches to a flex
 * container with an explicit page size and a `transform: rotate()` on the
 * sticker, which is needed to flip the content within the page.
 */

import { toast } from 'sonner';
import { printStickerViaUsb } from './usbPrinter';
import { getBrowserPrintConfig, printStickerViaBrowserPrint } from './browserPrint';

export function printJobSticker(job, settings) {
  if (!settings || !job) return;

  // Zebra Browser Print (per-PC opt-in). On failure offer the dialog via a toast
  // button — a fresh click is needed or Chrome blocks the popup.
  if (getBrowserPrintConfig().enabled) {
    printStickerViaBrowserPrint(job, settings).then((ok) => {
      if (!ok) toast('Print with the normal dialog instead?', {
        duration: 10000,
        action: { label: 'Print dialog', onClick: () => printStickerDialog(job, settings) },
      });
    });
    return;
  }

  // Direct USB path — bypasses Chrome's print dialog and the Windows driver
  // entirely. Only kicks in if the user has enabled it AND paired a Zebra.
  if (settings.sticker_use_direct_usb) {
    printStickerViaUsb(job, settings);
    return;
  }
  printStickerDialog(job, settings);
}

function printStickerDialog(job, settings) {
  if (settings.printer_sticker) {
    toast.info(`Send sticker to: ${settings.printer_sticker}`, { duration: 4000 });
  }

  const labelW = settings.sticker_width_mm || 40;
  const labelH = settings.sticker_height_mm || 28;
  const fontSizeJob = settings.sticker_font_size_job || 14;
  const fontSizeName = settings.sticker_font_size_name || 14;
  const fontSizePhone = settings.sticker_font_size_phone || 14;

  const rotation = parseInt(settings.sticker_rotation_deg, 10) || 0;
  const isPortrait = rotation === 90 || rotation === 270;
  const pageW = isPortrait ? labelH : labelW;
  const pageH = isPortrait ? labelW : labelH;

  const esc = (v) =>
    String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const stickerWindow = window.open('', '_blank', 'width=400,height=300');
  if (!stickerWindow) {
    toast.error('Popup blocked — allow popups for this site to print stickers');
    return;
  }

  // Build the CSS based on whether rotation is needed.
  // The rotation === 0 branch is intentionally the simple, original template
  // that prints correctly on a standard 40x28 Zebra setup.
  let css;
  if (rotation === 0) {
    css = `
@page { size: ${labelW}mm ${labelH}mm; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { font-family: Arial, sans-serif; }
.sticker {
  width: ${labelW}mm;
  height: ${labelH}mm;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  overflow: hidden;
  page-break-after: avoid;
  page-break-before: avoid;
  page-break-inside: avoid;
}
.job-number { font-size: ${fontSizeJob}pt; font-weight: bold; line-height: 1.2; margin-bottom: 1em; }
.customer-name { font-size: ${fontSizeName}pt; font-weight: bold; line-height: 1.2; }
.mobile { font-size: ${fontSizePhone}pt; font-weight: bold; line-height: 1.2; }`;
  } else {
    css = `
@page { size: ${pageW}mm ${pageH}mm; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
html { width: ${pageW}mm; height: ${pageH}mm; overflow: hidden; }
body {
  width: ${pageW}mm;
  height: ${pageH}mm;
  overflow: hidden;
  font-family: Arial, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  page-break-inside: avoid;
  page-break-after: avoid;
}
.sticker {
  width: ${labelW}mm;
  height: ${labelH}mm;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  overflow: hidden;
  flex: 0 0 auto;
  transform: rotate(${rotation}deg);
  transform-origin: center center;
}
.job-number { font-size: ${fontSizeJob}pt; font-weight: bold; line-height: 1.2; margin-bottom: 1em; }
.customer-name { font-size: ${fontSizeName}pt; font-weight: bold; line-height: 1.2; }
.mobile { font-size: ${fontSizePhone}pt; font-weight: bold; line-height: 1.2; }`;
  }

  // Auto-close script: triggers the print dialog inside the popup itself and
  // closes the window once the dialog is dismissed (printed or cancelled).
  // Using onafterprint (bound in the popup context) plus a matchMedia fallback
  // covers Chrome/Edge/Firefox on Windows/macOS. A 60s safety timeout ensures
  // the popup never lingers even if both events fail to fire.
  const autoCloseScript = `
<script>
(function() {
  var closed = false;
  function safeClose() {
    if (closed) return;
    closed = true;
    try { window.close(); } catch (e) {}
  }
  window.onafterprint = safeClose;
  if (window.matchMedia) {
    var mql = window.matchMedia('print');
    if (mql && mql.addListener) {
      mql.addListener(function(m) { if (!m.matches) setTimeout(safeClose, 100); });
    }
  }
  setTimeout(function() {
    try { window.focus(); window.print(); } catch (e) {}
  }, 250);
  // Safety net: if neither afterprint nor matchMedia fires within 60s, close.
  setTimeout(safeClose, 60000);
})();
</script>`;

  // Closing tags kept on same line as the sticker div to avoid trailing-whitespace
  // overflow that some browsers count as a phantom 2nd page.
  const html = `<!DOCTYPE html><html><head><title>Job Sticker</title><style>${css}
</style></head><body><div class="sticker"><div class="job-number">Job ${esc(job.job_number)}</div><div class="customer-name">${esc(job.customer_name)}</div><div class="mobile">${esc(job.mobile || '')}</div></div>${autoCloseScript}</body></html>`;

  stickerWindow.document.write(html);
  stickerWindow.document.close();
}
